import React, { ChangeEvent, useMemo, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import {
  AddInfiniteEligibleUsersResponse,
  InfiniteEligibility,
  InfiniteEligibleUserInput,
  addInfiniteEligibleUsers,
  updateInfiniteEligibility,
} from '~/services/LuxLoyaltyInfiniteApplicationService';

import { EMAIL_REGEX, normalizeHeader } from './infiniteEligibilityBulkMarkEmail';

type EntryMode = 'manual' | 'csv';

type ManualFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  registerInterestEmailSent: boolean;
};

type EligibilityConflict = AddInfiniteEligibleUsersResponse['result']['conflicts'][number];

const EMPTY_MANUAL_FORM: ManualFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  registerInterestEmailSent: false,
};

function parsePhoneNumber(value: string): Pick<InfiniteEligibleUserInput, 'phonePrefix' | 'phone'> | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const digits = trimmed.replace(/[^0-9]/g, '');

  if (trimmed.startsWith('+') && digits.startsWith('61')) {
    return { phonePrefix: '+61', phone: digits.slice(2) };
  }

  if (digits.startsWith('61') && digits.length > 9) {
    return { phonePrefix: '+61', phone: digits.slice(2) };
  }

  return { phonePrefix: '+61', phone: digits };
}

function validateEligibleUsers(users: Array<InfiniteEligibleUserInput>): Array<string> {
  const errors: Array<string> = [];

  users.forEach((user, index) => {
    const row = index + 1;

    if (!user.firstName.trim()) {
      errors.push(`Row ${row}: First name is required`);
    }
    if (!user.lastName.trim()) {
      errors.push(`Row ${row}: Last name is required`);
    }
    if (!EMAIL_REGEX.test(user.email.trim())) {
      errors.push(`Row ${row}: Valid email is required`);
    }
  });

  return errors;
}

function buildManualEligibleUser(form: ManualFormState): InfiniteEligibleUserInput {
  const phone = parsePhoneNumber(form.phoneNumber);

  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    email: form.email.trim().toLowerCase(),
    ...phone,
    registerInterestEmailSent: form.registerInterestEmailSent,
  };
}

function parseEligibleUsersCsv(file: File): Promise<Array<InfiniteEligibleUserInput>> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: normalizeHeader,
      complete: (result) => {
        if (result.errors.length > 0) {
          reject(new Error(`CSV parsing errors: ${result.errors.map((error) => error.message).join(', ')}`));
          return;
        }

        const users = result.data
          .map((row) => {
            const phone = parsePhoneNumber(row.phonenumber ?? row.phone ?? '');

            return {
              firstName: row.firstname?.trim() ?? '',
              lastName: row.lastname?.trim() ?? '',
              email: row.email?.trim().toLowerCase() ?? '',
              ...phone,
            };
          })
          .filter((user) => user.firstName || user.lastName || user.email || user.phone);
        const validationErrors = validateEligibleUsers(users);

        if (users.length === 0) {
          reject(new Error('CSV file is empty or has no valid data rows'));
          return;
        }

        if (validationErrors.length > 0) {
          reject(new Error(validationErrors.join('\n')));
          return;
        }

        resolve(users);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}

function EligibleUsersPreview({ users }: { users: Array<InfiniteEligibleUserInput> }) {
  if (users.length === 0) {
    return null;
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, maxHeight: 280 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>First name</TableCell>
            <TableCell>Last name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Phone</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user, index) => (
            <TableRow key={`${user.email}-${user.phone}-${index}`}>
              <TableCell>{user.firstName}</TableCell>
              <TableCell>{user.lastName}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                {user.phonePrefix} {user.phone}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ConflictValue({ title, user }: { title: string; user: InfiniteEligibleUserInput | InfiniteEligibility }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, flex: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Stack spacing={0.75}>
        <Typography variant="body2">{[user.firstName, user.lastName].filter(Boolean).join(' ') || '-'}</Typography>
        <Typography variant="body2" color="text.secondary">
          {user.email}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[user.phonePrefix, user.phone].filter(Boolean).join(' ') || '-'}
        </Typography>
        {'isRetracted' in user && (
          <Typography variant="body2" color={user.isRetracted ? 'error.main' : 'success.main'}>
            {user.isRetracted ? 'Retracted' : 'Active'}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function EligibilityConflictDialog({
  conflicts,
  index,
  open,
  isSubmitting,
  onKeepCurrent,
  onUpdate,
  onClose,
}: {
  conflicts: Array<EligibilityConflict>;
  index: number;
  open: boolean;
  isSubmitting: boolean;
  onKeepCurrent: () => void;
  onUpdate: () => void;
  onClose: () => void;
}) {
  const conflict = conflicts[index];

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Resolve eligibility conflict</DialogTitle>
      <DialogContent dividers>
        {conflict && (
          <Stack spacing={2}>
            <Alert severity="warning">
              Row {conflict.rowNumber} already has an eligibility record. Choose whether to keep the current record or
              update it with the incoming details.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Conflict {index + 1} of {conflicts.length}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <ConflictValue title="Current" user={conflict.existing} />
              <ConflictValue title="To be updated" user={conflict.incoming} />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Close
        </Button>
        <Button onClick={onKeepCurrent} disabled={isSubmitting}>
          Keep current
        </Button>
        <Button variant="contained" onClick={onUpdate} disabled={isSubmitting}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EligibleUsersDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<EntryMode>('manual');
  const [manualForm, setManualForm] = useState<ManualFormState>(EMPTY_MANUAL_FORM);
  const [csvUsers, setCsvUsers] = useState<Array<InfiniteEligibleUserInput>>([]);
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Array<EligibilityConflict>>([]);
  const [conflictIndex, setConflictIndex] = useState(0);
  const [selectedConflictUpdates, setSelectedConflictUpdates] = useState<Array<EligibilityConflict>>([]);
  const [isResolvingConflicts, setIsResolvingConflicts] = useState(false);

  const manualUser = useMemo(() => buildManualEligibleUser(manualForm), [manualForm]);
  const manualErrors = useMemo(() => validateEligibleUsers([manualUser]), [manualUser]);
  const usersToSubmit = mode === 'manual' ? [manualUser] : csvUsers;
  const canSubmit = mode === 'manual' ? manualErrors.length === 0 : csvUsers.length > 0 && parseError === null;

  const mutation = useMutation({
    mutationFn: () => addInfiniteEligibleUsers(usersToSubmit),
    onSuccess: (response) => {
      const { createdCount, conflicts: responseConflicts, invalidRows } = response.result;
      enqueueSnackbar(
        responseConflicts.length > 0
          ? `Eligible users saved. Created ${createdCount}. ${responseConflicts.length} conflict${
              responseConflicts.length === 1 ? '' : 's'
            } need review.`
          : `Eligible users saved. Created ${createdCount}.`,
        {
          variant: responseConflicts.length > 0 ? 'warning' : 'success',
        },
      );
      if (invalidRows.length > 0) {
        enqueueSnackbar(`${invalidRows.length} invalid row${invalidRows.length === 1 ? '' : 's'} were skipped.`, {
          variant: 'warning',
        });
      }
      onCreated?.();
      if (responseConflicts.length > 0) {
        setConflicts(responseConflicts);
        setConflictIndex(0);
        setSelectedConflictUpdates([]);
      } else {
        handleClose();
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const handleClose = () => {
    setMode('manual');
    setManualForm(EMPTY_MANUAL_FORM);
    setCsvUsers([]);
    setCsvFileName(null);
    setParseError(null);
    setConflicts([]);
    setConflictIndex(0);
    setSelectedConflictUpdates([]);
    setIsResolvingConflicts(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const submitSelectedConflictUpdates = async (updates: Array<EligibilityConflict>) => {
    if (updates.length === 0) {
      enqueueSnackbar('Eligibility conflicts reviewed.', { variant: 'info' });
      handleClose();
      return;
    }

    setIsResolvingConflicts(true);
    try {
      const results = await Promise.allSettled(
        updates.map((conflict) =>
          updateInfiniteEligibility(conflict.existing.id, {
            firstName: conflict.incoming.firstName,
            lastName: conflict.incoming.lastName,
            email: conflict.incoming.email,
            phonePrefix: conflict.incoming.phonePrefix,
            phone: conflict.incoming.phone,
            ...(conflict.incoming.registerInterestEmailSent !== undefined
              ? { registerInterestEmailSent: conflict.incoming.registerInterestEmailSent }
              : {}),
          }),
        ),
      );

      const failedUpdates = updates.filter((_, index) => results[index].status === 'rejected');
      const successCount = updates.length - failedUpdates.length;

      if (successCount > 0) {
        enqueueSnackbar(`Updated ${successCount} eligible user${successCount === 1 ? '' : 's'}.`, {
          variant: 'success',
        });
        onCreated?.();
      }

      if (failedUpdates.length === 0) {
        handleClose();
        return;
      }

      const failedEmails = failedUpdates
        .slice(0, 3)
        .map((conflict) => conflict.incoming.email)
        .join(', ');
      const additionalFailureCount = failedUpdates.length - 3;

      enqueueSnackbar(
        `Failed to update ${failedUpdates.length} eligible user${failedUpdates.length === 1 ? '' : 's'}: ${failedEmails}${
          additionalFailureCount > 0 ? ` and ${additionalFailureCount} more` : ''
        }.`,
        { variant: 'error' },
      );

      setConflicts(failedUpdates);
      setConflictIndex(0);
      setSelectedConflictUpdates([]);
    } finally {
      setIsResolvingConflicts(false);
    }
  };

  const moveToNextConflict = (updates: Array<EligibilityConflict>) => {
    const nextIndex = conflictIndex + 1;

    if (nextIndex < conflicts.length) {
      setConflictIndex(nextIndex);
      return;
    }

    void submitSelectedConflictUpdates(updates);
  };

  const keepCurrentConflict = () => {
    moveToNextConflict(selectedConflictUpdates);
  };

  const updateConflict = () => {
    const conflict = conflicts[conflictIndex];
    if (!conflict) {
      return;
    }

    const nextUpdates = [...selectedConflictUpdates, conflict];
    setSelectedConflictUpdates(nextUpdates);
    moveToNextConflict(nextUpdates);
  };

  const handleCsvFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setParseError(null);
    setCsvUsers([]);
    setCsvFileName(file?.name ?? null);

    if (!file) {
      return;
    }

    try {
      const users = await parseEligibleUsersCsv(file);
      setCsvUsers(users);
    } catch (error) {
      setParseError(error instanceof Error ? error.message : 'Failed to parse CSV file');
    }
  };

  const openCsvFilePicker = () => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const updateManualForm = (patch: Partial<ManualFormState>) => {
    setManualForm((current) => ({ ...current, ...patch }));
  };

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add eligible users</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Tabs value={mode} onChange={(_, next: EntryMode) => setMode(next)}>
            <Tab value="manual" label="Manual entry" />
            <Tab value="csv" label="Upload CSV" />
          </Tabs>

          {mode === 'manual' && (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2,
                }}
              >
                <TextField
                  label="First name"
                  value={manualForm.firstName}
                  onChange={(event) => updateManualForm({ firstName: event.target.value })}
                  required
                />
                <TextField
                  label="Last name"
                  value={manualForm.lastName}
                  onChange={(event) => updateManualForm({ lastName: event.target.value })}
                  required
                />
                <TextField
                  label="Email"
                  value={manualForm.email}
                  onChange={(event) => updateManualForm({ email: event.target.value })}
                  required
                />
                <TextField
                  label="Phone number"
                  placeholder="+61 412 345 678"
                  value={manualForm.phoneNumber}
                  onChange={(event) => updateManualForm({ phoneNumber: event.target.value })}
                />
              </Box>
              <Stack spacing={0.5} sx={{ '& .MuiFormControlLabel-root': { ml: 0 } }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={manualForm.registerInterestEmailSent}
                      onChange={(_, checked) => updateManualForm({ registerInterestEmailSent: checked })}
                    />
                  }
                  label="Register interest email sent"
                />
                <Box>
                  <FormControlLabel control={<Checkbox checked={false} disabled />} label="Concierge intro sent" />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                    Available once a related application is finalized.
                  </Typography>
                </Box>
              </Stack>
              {manualErrors.length > 0 && Object.values(manualForm).some(Boolean) && (
                <Alert severity="warning">
                  <Box component="ul" sx={{ pl: 2, my: 0 }}>
                    {manualErrors.map((error) => (
                      <Box component="li" key={error}>
                        {error}
                      </Box>
                    ))}
                  </Box>
                </Alert>
              )}
            </Stack>
          )}

          {mode === 'csv' && (
            <Stack spacing={2}>
              <Alert severity="info">
                Upload a CSV with columns: <strong>First Name</strong>, <strong>Last Name</strong>,{' '}
                <strong>Email</strong>, <strong>Phone Number</strong> (optional).
              </Alert>
              <Box>
                <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleCsvFileChange} />
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={openCsvFilePicker}
                  disabled={mutation.isPending}
                >
                  Choose CSV
                </Button>
                {csvFileName && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Selected: {csvFileName}
                  </Typography>
                )}
              </Box>
              {parseError && (
                <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
                  {parseError}
                </Alert>
              )}
              <EligibleUsersPreview users={csvUsers} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => mutation.mutate()} disabled={!canSubmit || mutation.isPending}>
          Add eligible {usersToSubmit.length === 1 ? 'user' : 'users'}
        </Button>
      </DialogActions>
      <EligibilityConflictDialog
        conflicts={conflicts}
        index={conflictIndex}
        open={conflicts.length > 0}
        isSubmitting={mutation.isPending || isResolvingConflicts}
        onKeepCurrent={keepCurrentConflict}
        onUpdate={updateConflict}
        onClose={handleClose}
      />
    </Dialog>
  );
}
